<script>
export default {
  title: 'Project 1b: Flow-of-Control Project',
  starterId: 'proj1b.zip'
}
</script>

<template>
  <section>
    <p>
      This programming assignment gives you experience with defining and using functions, parameters, and classes,
      the fundamental construct of object-oriented programming.
    </p>

    <article>
      <h2>Readings</h2>
      <ul>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Sections 2.4 -
          2.6
        </li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Section 3.4
        </li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Sections 4.5 -
          4.6
        </li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Section 5.4
        </li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Chapter 6
          (excluding Sections 6.3.5, 6.3.7, and 6.5)
        </li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Chapter 7</li>
        <li><a href="http://www.cs.duke.edu/csed/tapestry/"><cite>A Computer Science Tapestry</cite></a> Appendix D</li>
      </ul>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>Please download the starter code from above.</p>
    </article>

    <!--<article>-->
    <!--<h2>Problem</h2>-->
    <!--<p>Do <em>one</em> of the next two exercises:</p>-->
    <!--<ul>-->
    <!--<li>Cat and Mouse</li>-->
    <!--<li>Twenty-One</li>-->
    <!--</ul>-->
    <!--</article>-->

    <article>
      <h2>Cat and Mouse</h2>

      <figure>
        <img src="./images/cat-and-mouse.png" alt="Cat and Mouse" class="img-responsive center-block" width="560">
        <figcaption>Figure 1. The Cat and Mouse problem.</figcaption>
      </figure>

      <p>The scene is an urban park; a cat watches a mouse run around the base of
        a statue of the actors from Monty Python. Over the course of a second, the
        somewhat witless mouse moves one meter counterclockwise around the
        statue&rsquo;s base, which is circular and two meters in diameter. Every
        second, the cat pursues the mouse as follows: If the cat can see the mouse,
        the cat moves one meter toward the statue. If the cat can&rsquo;t see the
        mouse, the cat circles 1.25 meters counterclockwise around the statue.</p>

      <p>The cat plans eventually to get close enough to the mouse to make it a
        juicy lunch. The mouse by accident, however, may manage to keep completely
        out of sight of the cat, since both the cat and the mouse are moving
        counter-clockwise.</p>

      <p>Should the chase go on more than 30 seconds, however,
        the cat will get tired and wander off.</p>
    </article>

    <article>
      <h2>Your Task</h2>

      <p>
        Complete the program found in <code>cat+mouse.cpp</code> to produce a simulation
        of this situation and determine if the cat catches the mouse. Don't change any of
        the code already provided in that file. The program uses a <code>Position</code>
        class declared in <code>positions.h</code>; you are also to provide a file
        <code>positions.cpp</code> that correctly implements the operations of this class.
      </p>

      <p>
        The program first calls the <code>GetPositions</code> function to ask
        the user for the initial positions of the mouse (angle in degrees) and the
        cat (both radius and angle in degrees). You may assume that the user
        provides legal position values.
      </p>

      <p>
        The program then calls the <code>RunChase</code> function to simulate
        the chase using the rules listed above. (The cat moves first.) Your code
        should use member functions of the <code>Position</code> class where
        appropriate. It should print the position of the cat and mouse after each
        move, followed by the results of the chase (either the mouse becomes lunch,
        or the cat gives up after 30 minutes).
      </p>
    </article>

    <article>
      <h2>Useful Information for Writing positions.cpp</h2>

      <p>The <code>cos</code> function appears in the C++ math function library.
        Note that the argument to this function must be specified in
        <em>radians</em>, not degrees. A source file that uses any of the math
        functions must include the line</p>

      <pre v-highlightjs><code class="c">#include &lt;cmath&gt;</code></pre>

      <p>or, in an old C++ environment,</p>

      <pre v-highlightjs><code class="c">#include &lt;math.h&gt;</code></pre>

      <p>It's legal and appropriate to assign one object to another, for instance by saying</p>

      <pre v-highlightjs><code class="c">oldCatPosition = newCatPosition;</code></pre>

      <p>Note also that an object's member function (e.g. <code>IsBetween</code>)
        can access the private data of any other object of the same class.</p>
    </article>

    <article>
      <h2>Trigonometric Information</h2>
      <ul>
        <li>
          The cat sees the mouse if <br>
          <code>(cat radius) * cos (cat angle - mouse angle) >= 1.0</code> (angles in radian)
        </li>
        <li>
          When the cat circles distance d around the statue, its radius does not change, and the change in
          its angle can be calculated from the relationship <br>
          <code>d = angle * radius</code> (angles in radian)
        </li>
        <li>
          The cat catches the mouse when it (the cat) moves past the mouse while at the base of the statue,
          i.e. when the cat radius is 1.0 and the mouse angle lies between the old cat angle and the new
          cat angle. An angle B is between angles A and C in the following circumstances:<br>
          <code>cos (B - A) &gt; cos (C - A)</code>, and <code>cos (C - B) &gt; cos (C - A)</code> (angles in radian)<br>
          The difference C - A is assumed to be less than 90&deg;, or &pi;/2 radians.
        </li>
        <li>Note that the cat cannot move inside the statue&rsquo;s base; hence if the cat is, say, at radius 1.7 and sees the mouse, it can move in only 0.7 meters, up to the base of the statue.
        </li>
        <li>Remember that angles a, a + 2&pi;, a + 4&pi;, ... are all equal.</li>
      </ul>
    </article>

    <article>
      <h2>Testing</h2>

      <p>Test each function and method in your program with enough different
        inputs to show that it works. When testing a method, print out the input,
        the expected result, and the actual result.</p>

      <p>It would probably be helpful for debugging purposes to have a debug flag
        available that, when true, dumped the position and heading of all
        characters involved (cat and mouse) at each time step.</p>

      <p>You should test the methods of each Class in isolation, using
        appropriate test data, to see if they work independently before calling
        them from a driver function. Tutors may ask to see the results of these
        tests. Your test data should include the following values:</p>

      <p><i>(Note: Cat radius is calculated from the center of Statue)</i></p>

      <table class="table table-bordered table-striped">
        <tr>
          <th align="center">Cat Radius (m)</th>
          <th align="center">Cat Angle (degree)</th>
          <th align="center">Mouse Angle (degree)</th>
          <th align="center">Test Case Features</th>
        </tr>
        <tr>
          <td align="center">1.0</td>
          <td align="center">35.0&deg;</td>
          <td align="center">396.0&deg;</td>
          <td align="center">angle &gt; 360; mouse caught immediately</td>
        </tr>
        <tr>
          <td align="center">3.2</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center">negative angle;<br>
            cat radius has fractional part;<br>
            mouse caught at time 17</td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">0.0&deg;</td>
          <td align="center">45.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">150.0&deg;</td>
          <td align="center">240.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">4.0</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center"></td>
        </tr>
      </table>
      <p>...plus the following cases:</p>
      <ul>
        <li>cat catches mouse at time 30;</li>
        <li>cat would catch mouse at time 31 if the simulation ran that long;</li>
        <li>whatever other values are necessary to ensure that all statements in your program have been executed at least once.</li>
      </ul>

      <p>Be sure to check that the output makes sense for these tests, since the output is what most tutors will check first. Students are sometimes embarrassed to have a tutor point out that their programs have the cat moving deeper and deeper inside the statue, or moving away from the mouse instead of approaching it.</p>

    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>Correctly working code.</li>
        <li>
          No changes made to functions already provided in <code>cat+mouse.cpp</code>, or to the
          public interface in <code>positions.h</code>.
        </li>
        <li>Sufficient testing, with output sufficient to verify test correctness:
          <ul>
            <li>tests on specified values for cat and mouse positions;</li>
            <li>evidence of independent tests of all functions in <code>positions.cpp</code>;</li>
            <li>tests sufficient to exercise all statements in the program.</li>
          </ul>
        </li>
        <li>Listings of program text, tests, and test results.</li>
        <li>Adherence to CS 9F style standards:
          <ul>
            <li>appropriate use of indenting and white space;</li>
            <li>avoidance of "forbidden C++";</li>
            <li>variable and function names that reflect their use;</li>
            <li>informative comments at the head of each function;</li>
            <li>no routine more than twenty-four lines of code.</li>
          </ul>
        </li>
        <li>Clean case analysis and simple loop structuring.</li>
        <li>
          Suitable input prompts and informative output, including at least the positions of
          the cat and mouse after each move, and the final result of the chase.
        </li>
      </ul>
    </article>

    <!--<hr>-->

    <!--<article>-->
      <!--<h2>The Challenge 2: Twenty-One</h2>-->

      <!--<p>For this assignment, you complete a program to play the game of-->
        <!--"Twenty-one" (also called "Blackjack").</p>-->

      <!--<p>The general idea of Twenty-one is as follows. There are two players, the-->
        <!--<dfn>customer</dfn> and the <dfn>dealer</dfn>. The dealer deals a card-->
        <!--face down to the customer, then a card face up to herself (the-->
        <!--<dfn>up-card</dfn>), then another card to the customer and another card to-->
        <!--herself, both face down. In most cases, the customer may then ask for more-->
        <!--cards, one at a time. The dealer does the same, and the hand is resolved.-->
        <!--The object of the game is to be dealt a set of cards whose total value is-->
        <!--as close to 21 as possible without going over.</p>-->

      <!--<p>Cards are dealt from a deck of 52. There are four cards in the deck with-->
        <!--value 2, four with value 3, and so on up to value 9; there are sixteen-->
        <!--cards with value 10 (four 10's and twelve face cards: four jacks, four-->
        <!--queens, and four kings); and there are four cards ("aces") whose value may-->
        <!--be either 1 or 11 depending on the value of a player's remaining cards. For-->
        <!--both players, an ace counts 11 unless the hand total is more than 21, in-->
        <!--which case it counts 1.</p>-->

      <!--<p>For our purposes, the actual play of the game proceeds as follows. Each-->
        <!--player receives two cards as described above. If the dealer's cards total-->
        <!--21—that is, one has value 10 and the other is an ace—the dealer-->
        <!--has a "blackjack" and wins the hand. Otherwise, if the customer's cards-->
        <!--total 21, she has a blackjack and wins the hand. Otherwise, the customer-->
        <!--may ask for more cards one by one, until either she goes over 21—in-->
        <!--which case she loses, or "busts"—or has a total that she thinks is-->
        <!--high enough to beat the dealer. After the customer has stopped with a-->
        <!--total at most 21, the dealer draws, taking another card if her total is 16-->
        <!--or less, and stopping if her total is over 16. If the dealer busts, she-->
        <!--loses. If neither player has bust, the totals of the two sets of cards are-->
        <!--compared. If the customer's is higher, she wins; otherwise, the dealer-->
        <!--wins.</p>-->

      <!--<p>Productive strategies in Twenty-one involve taking account of the-->
        <!--dealer's up-card. For instance, it will be easier for the dealer to win if-->
        <!--her up-card is an ace or a face card.</p>-->
    <!--</article>-->

    <!--<article>-->
      <!--<h2>Examples</h2>-->

      <!--<p>Some annotated sample hands are shown below. Only the values of the cards-->
        <!--are listed, except for aces.</p>-->

      <!--<p>-->
        <!--Customer hand = 10 ace<br>-->
        <!--Dealer hand = ace 10<br>-->
        <!--Dealer has blackjack and wins immediately, even though the customer also has blackjack.-->
      <!--</p>-->

      <!--<p>-->
        <!--Customer hand = 10 ace<br>-->
        <!--Dealer hand = 4 9<br>-->
        <!--Customer has blackjack and wins immediately.-->
      <!--</p>-->

      <!--<p>-->
        <!--Customer hand = 4 ace<br>-->
        <!--Dealer hand = 4 9<br>-->
        <!--Customer continues, and is dealt a 7; her hand now totals 12.-->
      <!--</p>-->

      <!--<p>-->
        <!--Customer continues, and is dealt a 10; dealer wins.<br>-->
        <!--Customer hand = 4 ace<br>-->
        <!--Dealer hand = 4 9<br>-->
        <!--Customer continues, and is dealt a 7; her hand now totals 12.<br>-->
        <!--Customer continues, and is dealt an ace; her hand now totals 13.<br>-->
        <!--Customer continues, and is dealt a 5; her hand now totals 18.<br>-->
        <!--Dealer draws a 10 and loses.-->
      <!--</p>-->

      <!--<p>-->
        <!--Customer hand = 4 ace<br>-->
        <!--Dealer hand = 4 9<br>-->
        <!--Customer continues, and is dealt a 7; her hand now totals 12.<br>-->
        <!--Customer continues, and is dealt an ace; her hand now totals 13.<br>-->
        <!--Customer continues, and is dealt a 5; her hand now totals 18.<br>-->
        <!--Dealer's total is less than 17, so she continues, drawing a 3; her hand now totals 16.<br>-->
        <!--Dealer's total is less than 17, so she continues, drawing an ace; her hand now totals 17.<br>-->
        <!--Dealer must stand. Customer has more than dealer, so customer wins.-->
      <!--</p>-->

      <!--<p>-->
        <!--Customer hand = 4 ace<br>-->
        <!--Dealer hand = 4 9<br>-->
        <!--Customer continues, and is dealt a 3; her hand now totals 18.<br>-->
        <!--Dealer's total is less than 17, so she continues, drawing a 3; her hand now totals 16.<br>-->
        <!--Dealer's total is less than 17, so she continues, drawing a 5; her hand now totals 21.<br>-->
        <!--Dealer must stand. Dealer has more than customer, so dealer wins.-->
      <!--</p>-->
    <!--</article>-->

    <!--<article>-->
      <!--<h2>Problem</h2>-->

      <!--<p>Complete the program in the file <code>blackjack.cpp</code> to play games of Twenty-One. The program begins-->
        <!--each game by calling the <code>DealFirstFourCards</code> function, which initializes the customer and dealer-->
        <!--hands as described above. After checking for blackjacks, it calls the <code>ResultOfPlay</code> function to play-->
        <!--the game. You supply the code for these two functions. Don't change any of the code that's provided in the file.-->
      <!--</p>-->

      <!--<p>The program uses two classes, declared in <code>hands.h</code>, to represent the dealer and customer hands; you-->
        <!--are also to provide two versions of a file <code>hands.cpp</code> that correctly implements the operations of-->
        <!--these classes. These versions will differ only in the <code>CanDraw</code> member function of the <code>CustomerHand</code>-->
        <!--class. The idea is to to test two strategies for the customer, namely the dealer's strategy—draw to a total of-->
        <!--16 or under—and the following alternative:</p>-->

      <!--<blockquote class="note">-->
        <!--Take a card if and only if your hand total is less than 17 and the-->
        <!--dealer's up card is a 7, 8, 9, 10, or ace, or if your hand total is less-->
        <!--than 12 and the dealer's up card is a 2, 3, 4, 5, or 6. (The idea is that-->
        <!--in the second case, the dealer is much more likely to bust since there-->
        <!--are more 10's than any other card.)-->
      <!--</blockquote>-->

      <!--<p>Your program will play fifty games of Twenty-one with each strategy and-->
        <!--report how many games were won by dealer and customer in each run. A-->
        <!--blackjack should count as two wins.</p>-->

      <!--<article>-->
        <!--<h3>Files</h3>-->
        <!--<ul>-->
          <!--<li><a href="/~selfpace/doc/cpp/blackjack.cpp">blackjack.cpp</a></li>-->
          <!--<li><a href="/~selfpace/doc/cpp/cards.cpp">cards.cpp</a></li>-->
          <!--<li><a href="/~selfpace/doc/cpp/cards.h">cards.h</a></li>-->
          <!--<li><a href="/~selfpace/doc/cpp/hands.h">hands.h</a></li>-->
        <!--</ul>-->
      <!--</article>-->
    <!--</article>-->

    <!--<article>-->
      <!--<h2>System Information</h2>-->

      <!--<p>Your program will include calls to member functions of <code>Deck</code> and <code>Card</code> classes that we-->
        <!--supply in the file <code>cards.h</code>. The <code>Deck</code> constructor may be given a boolean argument that-->
        <!--says whether or not to produce debugging output when the deck is shuffled or a card is drawn; the code in <code>blackjack.cpp</code>-->
        <!--enables this deck option. One of the functions, <code>Shuffle</code>, should be called at the start of each-->
        <!--game; another, <code>Deal</code>, should be called to deal a card from the deck. The <code>Value</code> and-->
        <!--<code>IsAce</code> functions will help in evaluating a hand. The <code>Name</code> function should be used when-->
        <!--printing a hand, for instance as follows:</p>-->

      <!--<pre v-highlightjs><code class="c">cout &lt;&lt; card.Name();</code></pre>-->

      <!--<p>The file <code>cards.cpp</code> is available for inclusion in the executable version of your program. (The-->
        <!--techniques it uses won't be covered in CS 9F until the "Vectors" segment.) A command to create an executable-->
        <!--version of the program might then be</p>-->

      <!--<pre v-highlightjs><code class="bash">$ g++ -Wall -g blackjack.cpp hands.cpp cards.cpp</code></pre>-->
    <!--</article>-->

    <!--<article>-->
      <!--<h2>Miscellaneous Requirements</h2>-->

      <!--<p>-->
        <!--You do not need to store the individual cards in the hands (that requires an array).-->
        <!--You only need to maintain each hand's point total.-->
      <!--</p>-->

      <!--<p>-->
        <!--You should test the member functions of the <code>...Hand</code> classes in isolation,-->
        <!--using appropriate test data, to see if they work independently before calling them from-->
        <!--the program in <code>blackjack.cpp</code>. Tutors may ask to see the results of these-->
        <!--tests. Tests should also include whatever other values are necessary to ensure that all-->
        <!--statements in your program have been executed at least once. Be sure to check that your-->
        <!--output makes sense, since the output is what most tutors will check first. Students are-->
        <!--sometimes embarrassed to have a tutor point out that their programs have the customer-->
        <!--never drawing a card, or always valuing an ace as 1.-->
      <!--</p>-->

      <!--<p>-->
        <!--Some other useful tests involve the possibilities for valuing an ace. Test your program-->
        <!--on the following situations:-->
      <!--</p>-->

      <!--<ul>-->
        <!--<li>-->
          <!--a hand containing a single ace whose final value is 11;-->
        <!--</li>-->
        <!--<li>-->
          <!--a hand containing a single ace that initially is valued at 11, but whose value must-->
          <!--change to 1 because of subsequently drawn cards;-->
        <!--</li>-->
        <!--<li>-->
          <!--a hand containing a single ace that initially is valued at 1;-->
        <!--</li>-->
        <!--<li>-->
          <!--a hand containing more than one ace, one of which has a final value of 11;-->
        <!--</li>-->
        <!--<li>-->
          <!--a hand containing more than one ace, the first of which initially is valued at 11 but-->
          <!--whose value much change to 1 because of subsequently drawn cards;-->
        <!--</li>-->
        <!--<li>-->
          <!--a hand containing more than one ace, all of which have initial values of 1.-->
        <!--</li>-->
      <!--</ul>-->

      <!--<p>-->
        <!--Your program runs must also provide enough evidence that your simulation is accurate:-->
        <!--that hands are dealt and evaluated correctly, and that the rules and strategies described-->
        <!--above are correctly implemented. Include in your program sufficient output for the-->
        <!--tutor to be able to verify its correctness quickly. (One way to improve the readability-->
        <!--of your output is to indent the intermediate output and not to indent the result of-->
        <!--each game. For instance:-->
      <!--</p>-->

      <!--<pre v-highlightjs><code class="plain">Dealing 9 of spades-->
<!--Dealing 9 of clubs-->
<!--Dealing 4 of hearts-->
<!--Dealing 5 of clubs-->
<!--Customer hand: 2 cards, 0 aces counting 11, 18 total.-->
<!--Dealer hand: 2 cards, 0 aces counting 11, up card = 9 of clubs, total = 9-->
<!--Customer stands.-->
<!--Dealing 3 of spades-->
<!--Dealing 3 of hearts-->
<!--Dealing 9 of hearts-->
<!--WIN:  Dealer bust with 24</code></pre>-->

      <!--<p>-->
        <!--Your output should include examples of each case of the dealer's strategy, as well as-->
        <!--the customer's strategy as displayed in the following table:-->
      <!--</p>-->

      <!--<table class="table table-bordered">-->
        <!--<tbody>-->
        <!--<tr>-->
          <!--<th>Your Total</th>-->
          <!--<th>Dealer's Up Card</th>-->
          <!--<th>Your Total</th>-->
          <!--<th>Dealer's Up Card</th>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>&lt; 17</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
          <!--<td>&lt; 12</td>-->
          <!--<td>2 through 6</td>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>= 17</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
          <!--<td>= 12</td>-->
          <!--<td>2 through 6</td>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>&gt; 17</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
          <!--<td>&gt; 12</td>-->
          <!--<td>2 through 6</td>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>&lt; 17</td>-->
          <!--<td>2 through 6</td>-->
          <!--<td>&lt; 12</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>= 17</td>-->
          <!--<td>2 through 6</td>-->
          <!--<td>= 12</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
        <!--</tr>-->
        <!--<tr>-->
          <!--<td>&gt; 17</td>-->
          <!--<td>2 through 6</td>-->
          <!--<td>&gt; 12</td>-->
          <!--<td>7, 8, 9, 10, A</td>-->
        <!--</tr>-->
        <!--</tbody>-->
      <!--</table>-->

      <!--<p>-->
        <!--It is probably easier to provide this output by constructing cards by hand using the <code>ChangeCard</code>-->
        <!--function rather than relying on a shuffled deck to produce the right numbers.-->
      <!--</p>-->
      <!--<p>-->
        <!--Your code should call <code>...Hand</code> member functions where appropriate; code in-->
        <!--<code>blackjack.cpp</code> should not duplicate their functionality. You should not change the public interface-->
        <!--of the <code>...Hand</code> classes; you may, however, provide additional <code>private</code> member functions.-->
        <!--(Don't forget to provide the function prototypes for any functions you provide.) Your program should also adhere-->
        <!--to standards described in the section "Style guidelines" of this document. You don't need arrays to solve this-->
        <!--problem-->
      <!--</p>-->
    <!--</article>-->

    <!--<article>-->
      <!--<h2>Checklist</h2>-->
      <!--<ul>-->
        <!--<li>Correctly working code:-->
          <!--<ul>-->
            <!--<li>completion of <code>blackjack.cpp</code> without duplicating functionality in <code>...Hand</code>-->
              <!--member functions;-->
            <!--</li>-->
            <!--<li>completion of two versions of <code>hands.cpp</code> (one for each player strategy mentioned in the-->
              <!--assignment), without adding any public member functions to the <code>...Hand</code> classes.-->
            <!--</li>-->
          <!--</ul>-->
        <!--</li>-->
        <!--<li>Sufficient testing, with output sufficient to verify test correctness:-->
          <!--<ul>-->
            <!--<li>tests on specified strategy situations;</li>-->
            <!--<li>tests of hands that include aces as specified;</li>-->
            <!--<li>evidence of independent tests of all functions in <code>hands.cpp</code>;</li>-->
            <!--<li>tests sufficient to exercise all statements in the program.</li>-->
          <!--</ul>-->
        <!--</li>-->
        <!--<li>Adherence to CS 9F style standards:-->
          <!--<ul>-->
            <!--<li>appropriate use of indenting and white space;</li>-->
            <!--<li>avoidance of "forbidden C++"</li>-->
            <!--<li>variable and function names that reflect their use;</li>-->
            <!--<li>informative comments at the head of each function;</li>-->
            <!--<li>no routine more than twenty-four lines of code.</li>-->
          <!--</ul>-->
        <!--</li>-->
        <!--<li>Clean case analysis and simple loop structuring.</li>-->
        <!--<li>Suitable input prompts and informative output, including at least the final result of each game and the-->
          <!--total number of wins for a given strategy.-->
        <!--</li>-->
      <!--</ul>-->
      <!--<p><b>This is the end of challenge 2.</b></p>-->
    <!--</article>-->
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/cpp/FlowOfControl.vue